import { CSSProperties, FC, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { Checkbox, Form, FormInstance, Radio, RadioGroupProps } from 'antd';
import { FormBuilderData, FormBuilderFieldRadio } from 'types/formBuilder';
import { listIcons } from './FieldCheckbox';

const modeClassName: Record<FormBuilderFieldRadio['mode'], string> = {
  simple: '',
  pill: 'radio-group-pill',
  square: 'radio-group-square',
};

export function getResponsiveByFieldName(fieldName: string, listForms: FormBuilderData[]): any | undefined {
  if (!listForms) {
    return undefined;
  }

  if (fieldName === 'skip_estimate_approval') {
    const responsive = {
      mobile: {
        layout: 'column',
      },
      desktop: {
        layout: 'column',
      },
    };

    return responsive;
  }

  return listForms
    .flatMap((form: any) => form.form ?? [])
    .find((field: any) => typeof field === 'object' && 'name' in field && field.name === fieldName)?.responsive;
}

interface FieldRadioProps {
  id?: string;
  value?: RadioGroupProps['value'];
  data: FormBuilderFieldRadio;
  form: FormInstance;
  disabled: boolean;
  onChange?: RadioGroupProps['onChange'];
}

const FieldRadio: FC<FieldRadioProps> = ({ id, value, data, form, disabled, onChange }) => {
  const isMobile = window.innerWidth < 768;

  const listForms = useSelector((state: RootState) => state.resources.requestForms);
  const fallbackResponsive = getResponsiveByFieldName(data.name, listForms);

  const responsive = {
    mobile: data.responsive?.mobile ?? fallbackResponsive?.mobile ?? { layout: 'column' },
    desktop: data.responsive?.desktop ?? fallbackResponsive?.desktop ?? { layout: 'row' },
  };

  const layout = isMobile ? responsive.mobile : responsive.desktop;

  const ref = useRef<HTMLDivElement>(null);
  const boxRef = useRef<any>(null);

  const options = useMemo(() => {
    return data.options.map((item) => ({
      ...item,
      label: (
        <div style={{ textAlign: 'center' }}>
          {item.imageUrl ? (
            <>
              <img src={item.imageUrl} style={{ width: '40px', display: 'inline-block' }} alt="icon" />
              <br />
            </>
          ) : undefined}

          {item.icon ? (
            <>
              {listIcons[item.icon]}
              {data.mode === 'square' ? <br /> : undefined}
            </>
          ) : undefined}

          {item.label}
        </div>
      ),
    }));
  }, [data.mode, data.options]);

  useEffect(() => {
    if (ref.current) {
      if (data.name === 'skip_estimate_approval') {
        return () => {};
      }

      const unsubscribe = Array.from(ref.current.children).map((item) => {
        const input = item.querySelector('input');

        let timeout: NodeJS.Timeout;

        const listener = () => {
          const formValue = form.getFieldValue(data.name);

          let inputValue: any = input?.value;

          if (typeof formValue === 'boolean') {
            inputValue = Boolean(inputValue);
          }

          if (typeof formValue === 'number') {
            inputValue = Number(inputValue);
          }

          if (inputValue === form.getFieldValue(data.name)) {
            clearTimeout(timeout);

            timeout = setTimeout(() => {
              form.setFieldsValue({
                [data.name]: undefined,
              });
              boxRef.current?.input?.click();
            }, 0);
          }
        };

        item.addEventListener('click', listener);

        return () => {
          item.removeEventListener('click', listener);
        };
      });

      return () => {
        unsubscribe.forEach((callback) => callback());
      };
    }

    return () => {};
  }, [data.name, form]);

  const groupStyle = useMemo<CSSProperties | undefined>(() => {
    const baseStyle: CSSProperties = { gap: '8px' };

    switch (layout.layout) {
      case 'row':
        return {
          display: 'flex',
          flexWrap: 'wrap',
          ...baseStyle,
        };
      case 'column':
        return {
          display: 'flex',
          alignItems: isMobile ? 'inherit' : 'start',
          flexDirection: 'column',
          ...baseStyle,
        };
      case 'grid':
        return {
          display: 'grid',
          gridTemplateColumns: `repeat(${layout.cols || 1}, 1fr)`,
          ...baseStyle,
        };
      default:
        return undefined;
    }
  }, [layout, isMobile]);

  return (
    <>
      <Form.Item name={`${data.name}_force_render`} valuePropName="checked" hidden>
        <Checkbox ref={boxRef} />
      </Form.Item>

      <Radio.Group
        id={id}
        ref={ref}
        value={value}
        className={`${modeClassName[data.mode]}${disabled ? ' read-only' : ''}`}
        style={groupStyle}
        options={options}
        onChange={onChange}
      />
    </>
  );
};

export default FieldRadio;
